import { defineCatalog } from "@json-render/core";
import { schema } from "@json-render/react/schema";
import {
  type VizComponentName,
  vizComponentSchemas,
} from "@/lib/ai/visualization/views/component-schemas";

type VizCatalogComponentConfigs = {
  [K in VizComponentName]: {
    props: (typeof vizComponentSchemas)[K];
    description: string;
    slots?: string[];
  };
};

// runtime catalog は renderer 実装と同じ component schema を正本として扱う。
const vizRuntimeCatalogComponents = {
  VizCard: {
    props: vizComponentSchemas.VizCard,
    slots: ["default"],
    description:
      "Container card for one visualization panel with title, one short description line under the title, and a bottom footnote for axis/range/caveat notes. Keep exactly one analytical claim per panel and avoid mixing unrelated chart intents in the same card.",
  },
  TimeSeriesLine: {
    props: vizComponentSchemas.TimeSeriesLine,
    description:
      "Use for trend changes over time with one or multiple series, including period comparisons. Best when the x-axis is chronological and users need to read direction, slope, and crossover timing. Avoid when category ranking is the primary question or when stacked contribution is more important than line continuity.",
  },
  TimeSeriesGroupedLine: {
    props: vizComponentSchemas.TimeSeriesGroupedLine,
    description:
      "Use for long-form grouped trends where one line family must keep group identity across one or more metrics. Best when color should represent the group and line style should represent the metric on a shared time axis. Avoid when only one wide metric family is needed or when too many groups would overload the chart.",
  },
  TimeSeriesArea: {
    props: vizComponentSchemas.TimeSeriesArea,
    description:
      "Use for time-based magnitude and stacked contribution over time. Prefer this when users need cumulative movement or share-over-time interpretation across additive series. Avoid when precise point-to-point comparison is the main goal and a line/composed view would communicate values more clearly.",
  },
  TimeSeriesComposed: {
    props: vizComponentSchemas.TimeSeriesComposed,
    description:
      "Use when combining bars and lines on one time axis (for example volume and rate). Best for mixed-unit stories where one metric is volume-like and another is rate-like but both must share the same temporal context. Avoid when all series have the same semantic role and a simpler line/area chart is sufficient.",
  },
  ScatterPlot: {
    props: vizComponentSchemas.ScatterPlot,
    description:
      "Use to analyze relationships, clusters, and outliers between numeric metrics. Requires numeric x/y fields and is suited for correlation hypotheses rather than chronological trend reading. Avoid when either axis is categorical or when the user intent is top/bottom ordering.",
  },
  VizTable: {
    props: vizComponentSchemas.VizTable,
    description:
      "Use for audits, detailed row inspection, or configuration lists where exact values matter. Prefer this when reviewers need row-level traceability, deterministic sorting, and direct value verification. Avoid replacing with charts when precision and inspectability are the primary user goal.",
  },
  BarRanking: {
    props: vizComponentSchemas.BarRanking,
    description:
      "Use for ranking categories by a single metric (top or bottom contributors). Best when the question is who leads or lags and relative order is more important than temporal change. Avoid for multi-series category comparison or period-vs-period side-by-side analysis where BarComparison is a better fit.",
  },
  BarComparison: {
    props: vizComponentSchemas.BarComparison,
    description:
      "Use for comparing multiple metrics or periods across categories with grouped or stacked bars. Best for side-by-side current-vs-previous or multi-series category comparison with consistent units. Use stacked mode only when series are additive and avoid this view for single-metric leaderboard intent, which should use BarRanking.",
  },
} satisfies VizCatalogComponentConfigs;

export const vizRuntimeCatalog = defineCatalog(schema, {
  components: vizRuntimeCatalogComponents,
  actions: {},
});
